import { ChevronRightIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import type { Communication } from '../../../../services/communications';
import { formatCreatedDate } from '../../../../utils/dates';
import { channelDef } from '../communications/channels';
import { CommunicationStatusBadge } from '../communications/CommunicationStatusBadge';
import { PatientPillsCapped } from '../communications/PatientPillsCapped';
import { useRecentCommunications } from '../communications/useRecentCommunications';

// Same rounded-xl/border/shadow-sm treatment as the stats and category cards
// in this tab, for visual consistency across the row.
const CARD_CLASS = 'bg-white rounded-xl border border-gray-200 shadow-sm';
const SKELETON_ROW_COUNT = 3;

function DirectionTile({ communication }: { communication: Communication }) {
    const def = communication.lastEntry ? channelDef(communication.lastEntry.channel) : null;
    if (!def) {
        return (
            <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 bg-gray-100 text-gray-500">
                <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
            </div>
        );
    }
    const Icon = def.icon;
    return (
        <div
            className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: def.soft, color: def.tone }}
        >
            <Icon className="h-3.5 w-3.5" />
        </div>
    );
}

function OverviewCommunicationRow({
    communication,
    onOpen
}: {
    communication: Communication;
    onOpen: () => void;
}) {
    const lead = communication.title;

    return (
        <button
            type="button"
            onClick={onOpen}
            className="w-full flex items-start gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
        >
            <DirectionTile communication={communication} />
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[13px] font-semibold text-gray-900 truncate">{lead}</span>
                    <span className="text-[11px] text-gray-400 shrink-0">
                        {formatCreatedDate(communication.lastActivityAt)}
                    </span>
                    <span className="ml-auto shrink-0">
                        <CommunicationStatusBadge status={communication.status} />
                    </span>
                </div>
                {communication.lastEntry && (
                    <p className="text-[13px] text-gray-600 leading-snug line-clamp-2 mt-1">
                        Last by {communication.lastEntry.authorName}
                    </p>
                )}
                <div className="flex items-center gap-2 mt-1.5 min-w-0">
                    <span className="font-mono text-[11px] text-gray-400 shrink-0">
                        {communication.referenceNo}
                    </span>
                    <PatientPillsCapped patients={communication.patients} />
                </div>
            </div>
        </button>
    );
}

function SkeletonRow() {
    return (
        <div className="flex items-center gap-4 px-5 py-4">
            <div className="w-7 h-7 rounded-lg bg-gray-100 animate-pulse shrink-0" />
            <div className="flex-1 min-w-0 flex flex-col gap-2">
                <div className="h-3 w-1/3 rounded bg-gray-100 animate-pulse" />
                <div className="h-2.5 w-2/3 rounded bg-gray-100 animate-pulse" />
            </div>
        </div>
    );
}

interface OverviewRecentCommunicationsCardProps {
    practiceId: string;
    onGoToCommunicationsTab: () => void;
}

export function OverviewRecentCommunicationsCard({
    practiceId,
    onGoToCommunicationsTab
}: OverviewRecentCommunicationsCardProps) {
    const navigate = useNavigate();
    // Same fetch the create page's "Recent communications from this
    // practice" section uses - no thread is being edited here, so nothing is
    // excluded.
    const { lastCommunications, isLoading } = useRecentCommunications(practiceId, undefined);

    const goToNew = () => navigate(`/accounts/practices/${practiceId}/communications/new`);
    const goToCommunication = (communication: Communication) =>
        navigate(`/accounts/practices/${practiceId}/communications/${communication.id}`);

    return (
        <div className={CARD_CLASS}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                <h3 className="text-[15px] font-bold text-gray-800">Recent communications</h3>
                {!isLoading && lastCommunications.length > 0 && (
                    <button
                        type="button"
                        onClick={onGoToCommunicationsTab}
                        className="flex items-center gap-0.5 text-[13px] font-semibold text-gray-500 hover:text-gray-800 transition-colors"
                    >
                        All communications
                        <ChevronRightIcon className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
            {isLoading ? (
                <div className="py-1">
                    {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                        <SkeletonRow key={i} />
                    ))}
                </div>
            ) : lastCommunications.length === 0 ? (
                <div className="px-6 pb-8 pt-4 flex flex-col items-center gap-3 text-center">
                    <ChatBubbleLeftRightIcon className="h-10 w-10 text-gray-300" strokeWidth={1} />
                    <p className="text-sm text-gray-500">
                        No communications logged for this practice.
                    </p>
                    <button
                        type="button"
                        onClick={goToNew}
                        className="flex items-center gap-2 bg-gray-900 text-white font-semibold px-4 h-9 rounded-lg text-sm hover:bg-black transition-colors"
                    >
                        <ChatBubbleLeftRightIcon className="h-4 w-4" />
                        Log the first communication
                    </button>
                </div>
            ) : (
                <div>
                    {lastCommunications.map((communication) => (
                        <OverviewCommunicationRow
                            key={communication.id}
                            communication={communication}
                            onOpen={() => goToCommunication(communication)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
